/**
 * Detects and transforms hardcoded Shopify URLs from Builder.io content
 * into environment-specific URLs via configPublic.shopify.baseUrlMap / accountUrlMap.
 */
import { configPublic } from '@src/config.public'
import type { CountryCode, LocaleConfig } from '@src/i18n/locales'
import { locales } from '@src/i18n/locales'

const shopifyHostnameRegex = /^(account\.)?(shop(?:-([a-z]{2}))?(?:\.[a-z]+)?\.bodi\.com)$/i

const SHOPIFY_COUNTRY_MAP: Record<string, CountryCode> = {
  us: 'US',
  ca: 'CA',
  gb: 'GB',
}

type ShopifyHostInfo = { type: 'base' | 'account'; country: CountryCode }

/**
 * Parses a Shopify hostname into a type and country object.
 * Used to determine the type of Shopify URL to use.
 * 'shop-ca.bodi.com' → { type: 'base', country: 'CA' }
 * 'account.shop-gb.stage.bodi.com' → { type: 'account', country: 'GB' }
 */
function parseShopifyHostname(hostname: string): ShopifyHostInfo | null {
  const match = hostname.match(shopifyHostnameRegex)
  if (!match) return null

  const isAccount = !!match[1]
  const countrySuffix = match[3]?.toLowerCase()
  const country: CountryCode | undefined = countrySuffix ? SHOPIFY_COUNTRY_MAP[countrySuffix] : 'US'

  if (!country) return null

  return { type: isAccount ? 'account' : 'base', country }
}

/**
 * Returns the correct Shopify URL to use.
 * { type: 'base', country: 'CA' } → 'https://shop-ca.bodi.com'
 * { type: 'account', country: 'GB' } → 'https://account.shop-gb.bodi.com'
 */
function getShopifyConfigUrl(shopify: {
  type: 'base' | 'account'
  country: CountryCode
}): string | undefined {
  return shopify.type === 'account'
    ? configPublic.shopify.accountUrlMap[shopify.country]
    : configPublic.shopify.baseUrlMap[shopify.country]
}

/**
 * Checks if a hostname is a valid Shopify hostname.
 * 'shop-ca.stage.bodi.com' → true
 * 'www.bodi.com' → false
 */
export const isExternalShopifyLink = (hostname: string): boolean => {
  return parseShopifyHostname(hostname) !== null
}

/** Uses the redirect host directly so regional account domains like shop-gb are preserved. */
export function getShopifyAccountLogoutUrlFromRedirect(redirectUrl: URL): URL | null {
  const shopify = parseShopifyHostname(redirectUrl.hostname)
  if (shopify?.type !== 'account') return null

  const logoutUrl = new URL('/logout', redirectUrl.origin)
  const locale = redirectUrl.searchParams.get('locale')
  if (locale) logoutUrl.searchParams.set('locale', locale)

  const returnUrl = redirectUrl.searchParams.get('return_url')
  if (returnUrl) logoutUrl.searchParams.set('return_url', returnUrl)

  return logoutUrl
}

/**
 * All Shopify language path segments declared across the locale config — used to detect
 * and strip a leading language prefix when re-localizing a URL (e.g. `/en` → `/fr`).
 */
const knownShopifyLangSegments = Array.from(
  new Set(Object.values(locales).map((l) => l.shopifyLanguagePathSegment))
)

/** Strips any leading known Shopify language segment (e.g. `/fr/x` → `/x`). */
const stripShopifyLangSegment = (path: string): string => {
  for (const seg of knownShopifyLangSegments) {
    if (path === seg) return '/'
    if (path.startsWith(`${seg}/`)) return path.slice(seg.length)
  }
  return path
}

/** Sets the URL's language path segment. `/en` is omitted — Shopify's default. */
const applyShopifyStorefrontLocale = (url: URL, locale: LocaleConfig): void => {
  const stripped = stripShopifyLangSegment(url.pathname)
  const target = locale.shopifyLanguagePathSegment
  url.pathname = target === '/en' ? stripped : stripped === '/' ? target : `${target}${stripped}`
}

/**
 * Account doesn't honor path-prefix locales (`/fr/profile` → OAuth `locale=en-US`).
 * Uses `?locale=<lang>` query instead (cf. `update-password/page.tsx`).
 */
export const applyShopifyAccountLocale = (url: URL, locale: LocaleConfig): void => {
  url.pathname = stripShopifyLangSegment(url.pathname)
  if (locale.lang === 'en') {
    url.searchParams.delete('locale')
  } else {
    url.searchParams.set('locale', locale.lang)
  }
}

/**
 * Sets Shopify Markets context query params (`country`, `language`) on the URL.
 * Without these, Shopify can fall back to canonical English on first visit even when
 * the URL path declares a language — the params are an explicit context directive that
 * beats Accept-Language and missing session cookies. The params survive intermediate
 * redirects, so setting them on bodi.com → Shopify entry URLs (e.g. /bodcheckout)
 * propagates them all the way to Shopify's storefront.
 *
 * Storefront-only — Account URLs use `?locale=<lang>` instead.
 */
export const applyShopifyMarketsParams = (url: URL, locale: LocaleConfig): void => {
  url.searchParams.set('country', locale.countryCode)
  url.searchParams.set('language', locale.langUppercase)
}

/** True when the link's resolved shop host matches the locale's shop host. */
const domainMatchesLocale = (shopify: ShopifyHostInfo, locale: LocaleConfig) => {
  const urlConfigUrl = getShopifyConfigUrl(shopify)
  const localeConfigUrl = getShopifyConfigUrl({
    type: shopify.type,
    country: locale.countryCode,
  })

  if (!urlConfigUrl || !localeConfigUrl) return false
  return new URL(urlConfigUrl).hostname === new URL(localeConfigUrl).hostname
}

/**
 * Replaces the Shopify hostname on a URL object in-place. Path and query are preserved.
 * 'https://shop-ca.bodi.com/products/shakeology' → 'https://shop-ca.stage.bodi.com/products/shakeology'
 *
 * With a `locale`, also re-localizes per the Shopify surface's convention:
 * - Storefront (`shop-ca.bodi.com`): language path (when hosts match) + Markets params
 *     `/products/x` + fr-CA → `/fr/products/x?country=CA&language=FR`
 *     `/pages/x` + fr-CA on US shop → `/pages/x?country=CA&language=FR` (no `/fr`)
 * - Account (`account.shop-ca.bodi.com`): `?locale=<lang>` query, no path prefix
 *     `/profile` + fr-CA → `/profile?locale=fr`
 */
export const transformExternalShopifyLink = (url: URL, locale?: LocaleConfig): void => {
  const shopify = parseShopifyHostname(url.hostname)
  if (!shopify) return

  const configUrl = getShopifyConfigUrl(shopify)
  if (configUrl) {
    url.hostname = new URL(configUrl).hostname
  }

  if (!locale) return
  if (shopify.type === 'account') {
    applyShopifyAccountLocale(url, locale)
  } else {
    if (domainMatchesLocale(shopify, locale)) {
      applyShopifyStorefrontLocale(url, locale)
    }
    applyShopifyMarketsParams(url, locale)
  }
}

/** Matches a Shopify origin (protocol + hostname) inside a text string. */
export const shopifyTextRegex =
  /https?:\/\/(account\.)?(shop(?:-([a-z]{2}))?(?:\.[a-z]+)?\.bodi\.com)(?=[/:?#\s]|$)/gi

/**
 * Replaces a Shopify hostname in a text string with the correct Shopify URL.
 * @example 'Visit https://shop-ca.bodi.com/collections' → 'Visit https://shop-ca.stage.bodi.com/collections'
 * @example 'Go to https://account.shop-gb.bodi.com/orders' → 'Go to https://account.shop-gb.stage.bodi.com/orders'
 */
export const shopifyTextReplacer = (
  match: string,
  accountPrefix: string | undefined,
  _shopHostname: string,
  countrySuffix: string | undefined
): string => {
  const isAccount = !!accountPrefix
  const country: CountryCode | undefined = countrySuffix
    ? SHOPIFY_COUNTRY_MAP[countrySuffix.toLowerCase()]
    : 'US'
  if (!country) return match

  const configUrl = isAccount
    ? configPublic.shopify.accountUrlMap[country]
    : configPublic.shopify.baseUrlMap[country]

  return configUrl?.replace(/\/$/, '') ?? match
}
